import { useEffect } from "react";
import { usePocketBase } from "@/context/pocketbase-context";
import { useQuery } from "@tanstack/react-query";

export interface MetricData {
  date: string;
  coherence: number;
  cohesion: number;
  contundence: number;
  lexicalResource: number;
  pronunciation: number;
  fluency: number;
  duration: number;
}

export interface MetricTotalData {
  date: string;
  total: number | null;
}

export interface LanguageMetricsResponse {
  data: MetricData[];
  period: string;
  language: string;
  mode: string;
}

const coreUrl = import.meta.env.VITE_REACT_APP_POCKETBASE_URL;

const fetchLanguageMetrics = async (
  period: string,
  mode: string,
  token: string,
  language?: string,
): Promise<LanguageMetricsResponse> => {
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${token}`);

  const queryParams = new URLSearchParams();
  if (period) queryParams.append("days_ago", period);
  if (language) queryParams.append("language_filter", language);
  if (mode) queryParams.append("mode_filter", mode);

  const response = await fetch(
    `${coreUrl}/api/metrics?` + queryParams.toString(),
    {
      method: "GET",
      headers: headers,
    },
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return response.json() as Promise<LanguageMetricsResponse>;
};

export const useLanguageMetrics = (
  period: string,
  mode: string,
  language?: string,
) => {
  const { token } = usePocketBase();

  useEffect(() => {
    console.log("Fetching language metrics with params:", {
      period,
      mode,
      language,
    });
  }, [period, mode, language]);

  return useQuery({
    queryKey: ["language-metrics", period, language, mode],
    queryFn: () => fetchLanguageMetrics(period, mode, token, language),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!language && !!token, // Only run if language and token are available
  });
};
